import { useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';

import { FunctionsEditor } from '#screens/functions-editor/functions-editor.js';
import { useTRPC } from '#src/trpc.js';
import { useDataElTagGenerator } from '#src/utils/use-data-el-tag-generator.js';
import { useAutoSave } from '#utils/use-auto-save/use-auto-save.js';

export const Route = createFileRoute(
	'/_authorization-gate/package-store/$packageFamilyId/packages/$packageId/functions/',
)({
	staticData: {
		navigation: {
			label: 'Package: Functions',
			keywords: ['functions', 'busses'],
		},
	},
	loader: ({ context: { queryClient, getPackageOptions } }) => {
		void queryClient.prefetchQuery(getPackageOptions);
		const cached = queryClient.getQueryData(getPackageOptions.queryKey);
		return { pkg: cached?.pkg };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title:
					(loaderData?.pkg ? loaderData.pkg.manufacturerPartNumber + ': ' : '') +
					'Functions • Package Editor • Cadstrom',
			},
		],
	}),
	component: FunctionsTab,
});

function FunctionsTab() {
	const queryClient = useQueryClient();
	const { getPackageOptions } = Route.useRouteContext();
	const trpc = useTRPC();

	const {
		data: { pkg },
	} = useSuspenseQuery(getPackageOptions);

	const { mutateAsync: updatePackage, isPending: updatePackageIsPending } = useMutation(
		trpc.package.update.mutationOptions({
			onSuccess: (res) => {
				// Merge rather than replace: `isConfidential` is derived from the package family's owner and
				// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
				queryClient.setQueryData(getPackageOptions.queryKey, (previous) =>
					previous === undefined ? undefined : { ...previous, pkg: res.pkg },
				);

				setServerFunctions(res.pkg.functions);
			},
		}),
	);

	const {
		draftState: draftFunctions,
		setDraftState: setDraftFunctions,
		setServerState: setServerFunctions,
		isDirty,
		handleSave,
		saveAreaContainerRef,
	} = useAutoSave({
		initialState: pkg.functions,
		mutationKeys: trpc.package.update.mutationKey(),
		saveFn: async (draftFunctions) =>
			await updatePackage({
				id: pkg.id,
				data: {
					functions: { set: draftFunctions },
				},
			}),
	});

	const el = useDataElTagGenerator(FunctionsTab);
	return (
		<div {...el()} ref={saveAreaContainerRef} className='flex min-h-0 grow basis-0 px-3 py-2'>
			<FunctionsEditor
				functions={draftFunctions}
				resources={pkg.resources}
				signals={pkg.signals}
				onChange={setDraftFunctions}
				showSaveButton={{
					saveDisabled: !isDirty,
					onClickSave: handleSave,
					isSaving: updatePackageIsPending,
				}}
			/>
		</div>
	);
}
